// src/components/public/InvitationHandler.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { formatAuthors } from '../utils/authors';
import { useAuth } from '../context/AuthContext';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  Mail,
  FileText,
  User,
  Calendar,
  BookOpen,
  Eye,
  EyeOff,
  Check,
  X,
  Clock,
  MapPin,
  Building,
  BookMarked,
  Ban,
  Hourglass,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ensureLeading, sanitizeEmail } from '../utils/utils.global';

const InvitationHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Parse URL parameters
  const queryParams = new URLSearchParams(location.search);
  const action = location.pathname.includes('accept') ? 'accept' : 'decline';
  const type = queryParams.get('type'); // 'reviewer' or 'editor'
  const articleId = queryParams.get('id');
  const email = sanitizeEmail(queryParams.get('email'));
  const token = queryParams.get('token');
  const redirectPath = ensureLeading(queryParams.get("url"))

  // State
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [userExists, setUserExists] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [authors, setAuthors] = useState([]);
  const [invitationStatus, setInvitationStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);
  
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtherDiscipline, setShowOtherDiscipline] = useState(false);

  // Discipline options (matching AuthorSignup)
  const disciplineOptions = [
    'Agricultural sciences',
    'Allied Health Sciences',
    'Anthropology',
    'Arts',
    'Biology',
    'Chemistry',
    'Climate Change',
    'Computer and Information Sciences',
    'Earth Sciences',
    'Engineering and Technology',
    'Environmental Sciences',
    'Epidemiology',
    'Humanities',
    'Leadership and Management',
    'Life Sciences',
    'Materials Science',
    'Mathematics',
    'Medicine',
    'Physics and Astronomy',
    'Politics',
    'Public & Global Health',
    'Social and Behavioral Sciences',
    'Statistics',
    'Theology',
    'Other'
  ];

  // Form state for account creation (matching AuthorSignup fields)
  const [formData, setFormData] = useState({
    prefix: '',
    firstName: '',
    lastName: '',
    otherName: '',
    email: email || '',
    orcid: '',
    affiliation: '',
    affiliationCountry: '',
    affiliationCity: '',
    discipline: '',
    otherDiscipline: '',
    reviewAvailability: 'yes', // Default to yes for reviewers
    password: '',
    confirmPassword: ''
  });

  const [formErrors, setFormErrors] = useState({});

  // Handle discipline change
  useEffect(() => {
    if (formData.discipline === 'Other') {
      setShowOtherDiscipline(true);
    } else {
      setShowOtherDiscipline(false);
    }
  }, [formData.discipline]);

  // Clear messages after timeout
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Fetch invitation and submission data
  useEffect(() => {
    const fetchInvitationData = async () => {
      if (!articleId || !email || !token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // First, check if user exists
        const userCheckResponse = await axios.post('/api/personnel/invitation/check-user', {
          email,
          type
        });

        setUserExists(userCheckResponse.data.exists);

        // Fetch invitation details with submission data
        const invitationResponse = await axios.post('/api/personnel/invitation/details', {
          articleId,
          email,
          token
        });

        // Check if response has submission data
        if (invitationResponse.data.submission) {
          setSubmissionData(invitationResponse.data.submission);
          
          // Parse authors if needed
          if (invitationResponse.data.submission?.authors_list) {
            setAuthors(invitationResponse.data.submission.authors_list);
          } else if (invitationResponse.data.submission?.authors) {
            try {
              const parsedAuthors = typeof invitationResponse.data.submission.authors === 'string'
                ? JSON.parse(invitationResponse.data.submission.authors)
                : invitationResponse.data.submission.authors;
              setAuthors(parsedAuthors);
            } catch (e) {
              console.error('Error parsing authors:', e);
            }
          }
        }

        // Handle invitation status
        if (invitationResponse.data.success) {
          // Active invitation found
          setInvitationData(invitationResponse.data.invitation);
          setInvitationStatus(invitationResponse.data.invitation.status);
          
          // Check if expired
          const expiresAt = new Date(invitationResponse.data.invitation.expires_at);
          const now = new Date();
          if (expiresAt < now) {
            setIsExpired(true);
            setStatusMessage('This invitation has expired');
          }
        } else {
          // Check if there's a status in the error response
          if (invitationResponse.data.status) {
            setInvitationStatus(invitationResponse.data.status);
            setIsProcessed(true);
            
            // Set appropriate message based on status
            switch(invitationResponse.data.status) {
              case 'accepted':
                setStatusMessage('You have already accepted this invitation');
                
                break;
              case 'rejected':
                setStatusMessage('You have already declined this invitation');
                break;
              case 'expired':
                setIsExpired(true);
                setStatusMessage('This invitation has expired');
                break;
              default:
                setStatusMessage(invitationResponse.data.message || 'Invitation already processed');
            }
          } else {
            setError(invitationResponse.data.message || 'Invalid invitation');
          }
        }

      } catch (err) {
        console.error('Error fetching invitation data:', err);
        
        // Check if we got submission data even with error
        if (err.response?.data?.submission) {
          setSubmissionData(err.response.data.submission);
        }
        
        // Check if there's a status in the error response
        if (err.response?.data?.status) {
          setInvitationStatus(err.response.data.status);
          setIsProcessed(true);
          
          switch(err.response.data.status) {
            case 'accepted':
              setStatusMessage('You have already accepted this invitation');
              break;
            case 'rejected':
              setStatusMessage('You have already declined this invitation');
              break;
            case 'expired':
              setIsExpired(true);
              setStatusMessage('This invitation has expired');
              break;
            default:
              setStatusMessage(err.response.data.message || 'Invitation already processed');
          }
        } else {
          const status = err.response?.status;
          if (err.response?.data?.message) {
            setError(err.response.data.message);
          } else if (status === 404) {
            setError('Invitation not found. The link may be invalid or the manuscript has been removed.');
          } else if (status === 500) {
            setError('Server error. Please try again later or contact support.');
          } else if (!err.response) {
            setError('Network error. Please check your internet connection.');
          } else {
            setError('Failed to load invitation details');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvitationData();
  }, [articleId, email, token, type]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle radio change for review availability
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate Step 1: Personal Information
  const validateStep1 = () => {
    const errors = {};

    if (!formData.prefix) errors.prefix = 'Prefix is required';
    if (!formData.firstName) errors.firstName = 'First name is required';
    if (!formData.lastName) errors.lastName = 'Last name is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate Step 2: Professional Information
  const validateStep2 = () => {
    const errors = {};

    if (!formData.affiliation) errors.affiliation = 'Affiliation is required';
    if (!formData.affiliationCountry) errors.affiliationCountry = 'Country is required';
    if (!formData.affiliationCity) errors.affiliationCity = 'City is required';
    if (!formData.discipline) errors.discipline = 'Discipline is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate Step 3: Account Information
  const validateStep3 = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle next step
  const handleNextStep = () => {
    let isValid = false;
    
    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    }
    
    if (isValid) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  // Handle previous step
  const handlePreviousStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  // Process account creation separately
  const createAccount = async () => {
    if (!validateStep3()) {
      return false;
    }

    setProcessing(true);

    // Prepare final data with discipline handling
    const submitData = {
      ...formData,
      discipline: formData.discipline === 'Other' ? formData.otherDiscipline : formData.discipline,
      articleId,
      token,
      type
    };

    try {
      const response = await axios.post(`/api/personnel/invitation/create-${type}-account`, submitData);

      if (response.data.status === 'success') {
        setSuccessMessage(response.data.message || 'Account created successfully');
        setSuccess('Account created successfully');
        return true;
      } else if (response.data.status === 'partial_success') {
        // Account created but invitation acceptance failed
        setSuccessMessage(response.data.message || 'Account created but please try accepting the invitation again');
        setSuccess('Account created but please try accepting the invitation again');
        return true;
      } else {
        setError(response.data.message || 'Failed to create account');
        return false;
      }
    } catch (err) {
      console.error('Error creating account:', err);
      const status = err.response?.status;
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (status === 404) {
        setError('Invitation not found. The link may be invalid or the manuscript has been removed.');
      } else if (status === 500) {
        setError('Server error. Please try again later or contact support.');
      } else if (!err.response) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError('Failed to create account');
      }
      return false;
    } finally {
      setProcessing(false);
    }
  };

  // Process invitation separately (accept/decline). Returns
  // { processed, redirected } so callers know whether navigation already
  // happened (auto-login on accept) or a fallback redirect is needed.
  const processInvitationOnly = async () => {
    // Don't process if invitation is already processed or expired
    if (isProcessed || isExpired) {
      return { processed: false, redirected: false };
    }

    setProcessing(true);
    try {
      const endpoint = action === 'accept' 
        ? `/api/personnel/invitation/${type}/accept` 
        : `/api/personnel/invitation/${type}/decline`;

      const response = await axios.post(endpoint, {
        articleId,
        email,
        token
      });

      if (response.data.status === 'success') {
        const message = response.data.message || `Invitation ${action}ed successfully`;
        setSuccessMessage(message);
        setSuccess(message);

        // When an account already exists the backend returns a session — log
        // the user in and send them straight to the designated page (reviewer
        // paper or editors dashboard) instead of the login form.
        if (action === 'accept' && response.data.token && response.data.user) {
          const role = response.data.user.role || (type === 'editor' ? 'editor' : 'author');
          const loginResult = await login(email, '', role, response.data.token, response.data.user);
          if (loginResult.success) {
            const redirectTo = redirectPath || ensureLeading(response.data.redirectTo) || (type === 'editor' ? '/editors/dashboard' : '/reviewerdash');
            window.location.href = redirectTo;
            return { processed: true, redirected: true };
          }
        }

        // Fallback: show a success screen, then redirect
        setTimeout(() => {
          if (action === 'accept') {
            navigate(`/portal/login?returnUrl=${redirectPath}`, { state: { message } });
          } else {
            navigate('/');
          }
        }, 3000);
        return { processed: true, redirected: false };
      } else if (response.data.status === 'info' && response.data.requiresAccount) {
        setShowCreateAccount(true);
        return { processed: false, redirected: false };
      } else {
        setError(response.data.message || `Failed to ${action} invitation`);
        return { processed: false, redirected: false };
      }
    } catch (err) {
      console.error(`Error ${action}ing invitation:`, err);
      
      const status = err.response?.status;
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (status === 404) {
        setError('Invitation not found. The link may be invalid or the manuscript has been removed.');
      } else if (status === 500) {
        setError('Server error. Please try again later or contact support.');
      } else if (!err.response) {
        setError('Network error. Please check your internet connection.');
      } else {
        setError(`Failed to ${action} invitation`);
      }
      return { processed: false, redirected: false };
    } finally {
      setProcessing(false);
    }
  };

  // Main handler for invitation flow
  const handleInvitationAction = async () => {
    // If user doesn't exist, show account creation form
    if (!userExists) {
      setShowCreateAccount(true);
      return;
    }

    // Process invitation directly. When an account exists the backend returns
    // a session and processInvitationOnly logs the user in and redirects to the
    // designated page; otherwise the fallback redirect runs inside it.
    await processInvitationOnly();
  };

  // Handle account creation and then invitation
  const handleCreateAndAccept = async (e) => {
    e.preventDefault();
    
    // First create the account
    const accountCreated = await createAccount();
    
    if (accountCreated) {
      // Then process the invitation
      const result = await processInvitationOnly();
      
      if (result.processed && !result.redirected) {
        // Redirect after successful both operations
        setTimeout(() => {
          navigate(`/portal/login?returnUrl=${redirectPath}`, { 
            state: { message: 'Account created and invitation accepted successfully. Please log in.' }
          });
        }, 3000);
      } else if (!result.processed) {
        // Account created but invitation failed - still show success but with note
        setSuccess('Account created successfully. You can now log in and accept the invitation from your dashboard.');
        setTimeout(() => {
          navigate(`/portal/login?returnUrl=${redirectPath}`);
        }, 5000);
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Check if buttons should be disabled
  const isActionDisabled = () => {
    return processing || isProcessed || isExpired || !invitationData;
  };

  // Get button text based on state
  const getButtonText = () => {
    if (processing) return 'Processing...';
    if (isExpired) return 'Invitation Expired';
    if (isProcessed) {
      if (invitationStatus === 'accepted') return 'Already Accepted';
      if (invitationStatus === 'rejected') return 'Already Declined';
      return 'Already Processed';
    }
    if (!invitationData) return 'Invitation Unavailable';
    return action === 'accept' ? 'Accept Invitation' : 'Decline Invitation';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  // Error state (no submission data and error)
  if (error && !submissionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {action === 'accept' ? 'Invitation Accepted!' : 'Invitation Declined'}
          </h2>
          <p className="text-gray-600 mb-6">{successMessage || success}</p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader className="w-4 h-4 animate-spin" />
            <p>Redirecting you {action === 'accept' ? 'to login' : 'home'}...</p>
          </div>
        </div>
      </div>
    );
  }

  // Account creation form (multi-step, matching AuthorSignup)
  if (showCreateAccount) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Error/Success messages inside the form */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
              <p className="text-sm text-green-700">{successMessage || success}</p>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header */}
            <div className="bg-[--asfi-blue] px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Create Your {type === 'reviewer' ? 'Reviewer' : 'Editor'} Account
              </h2>
              <p className="text-purple-100 text-sm mt-1">
                Please complete your account information to {action} the invitation
              </p>
            </div>

            {/* Manuscript Info Summary */}
            {submissionData && (
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="font-medium text-gray-700 mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-purple-600" />
                  Manuscript Details
                </h3>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Title:</span> {submissionData.title}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">ID:</span> {articleId}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Type:</span> {submissionData.article_type || 'Research Article'}
                </p>
              </div>
            )}

            {/* Progress Steps */}
            <div className="px-6 pt-6">
              <div className="flex items-center justify-between">
                {[1, 2, 3].map((step) => (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        step < currentStep 
                          ? 'bg-green-500 text-white'
                          : step === currentStep
                          ? 'bg-[--asfi-blue] text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {step < currentStep ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          step
                        )}
                      </div>
                      <span className="text-xs mt-2 text-gray-600">
                        {step === 1 && 'Personal'}
                        {step === 2 && 'Professional'}
                        {step === 3 && 'Account'}
                      </span>
                    </div>
                    {step < 3 && (
                      <div className={`flex-1 h-1 mx-2 ${
                        step < currentStep ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleCreateAndAccept} className="p-6">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  {/* Prefix and Name Row */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                    <div className="md:col-span-1">
                      <label htmlFor="prefix" className="block text-sm font-medium text-gray-700 mb-1">
                        Prefix <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="prefix"
                        name="prefix"
                        value={formData.prefix}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          formErrors.prefix ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="">Select Prefix</option>
                        <option value="Prof.">Prof.</option>
                        <option value="Dr.">Dr.</option>
                        <option value="Mr.">Mr.</option>
                        <option value="Mrs.">Mrs.</option>
                        <option value="Ms.">Ms.</option>
                        <option value="Miss">Miss</option>
                      </select>
                      {formErrors.prefix && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.prefix}</p>
                      )}
                    </div>

                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="First Name"
                        />
                        {formErrors.firstName && (
                          <p className="mt-1 text-xs text-red-500">{formErrors.firstName}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                            formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Last Name"
                        />
                        {formErrors.lastName && (
                          <p className="mt-1 text-xs text-red-500">{formErrors.lastName}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="otherName" className="block text-sm font-medium text-gray-700 mb-1">
                          Other Name
                        </label>
                        <input
                          type="text"
                          id="otherName"
                          name="otherName"
                          value={formData.otherName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Other Name (Optional)"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email and ORCID */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={email}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-600"
                      />
                    </div>

                    <div>
                      <label htmlFor="orcid" className="block text-sm font-medium text-gray-700 mb-1">
                        ORCID ID (Optional)
                      </label>
                      <input
                        type="text"
                        id="orcid"
                        name="orcid"
                        value={formData.orcid}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="ORCID ID"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Professional Information */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  {/* Affiliation Details */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div>
                      <label htmlFor="affiliation" className="block text-sm font-medium text-gray-700 mb-1">
                        Affiliation <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="affiliation"
                        name="affiliation"
                        value={formData.affiliation}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          formErrors.affiliation ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Institution/Organization"
                      />
                      {formErrors.affiliation && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.affiliation}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="affiliationCountry" className="block text-sm font-medium text-gray-700 mb-1">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="affiliationCountry"
                        name="affiliationCountry"
                        value={formData.affiliationCountry}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          formErrors.affiliationCountry ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Country"
                      />
                      {formErrors.affiliationCountry && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.affiliationCountry}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="affiliationCity" className="block text-sm font-medium text-gray-700 mb-1">
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="affiliationCity"
                        name="affiliationCity"
                        value={formData.affiliationCity}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                          formErrors.affiliationCity ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="City"
                      />
                      {formErrors.affiliationCity && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.affiliationCity}</p>
                      )}
                    </div>
                  </div>

                  {/* Discipline */}
                  <div>
                    <label htmlFor="discipline" className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Discipline <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="discipline"
                      name="discipline"
                      value={formData.discipline}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        formErrors.discipline ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a Discipline</option>
                      {disciplineOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {formErrors.discipline && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.discipline}</p>
                    )}
                  </div>

                  {/* Other Discipline Input */}
                  {showOtherDiscipline && (
                    <div>
                      <label htmlFor="otherDiscipline" className="block text-sm font-medium text-gray-700 mb-1">
                        Please Specify Your Discipline <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="otherDiscipline"
                        name="otherDiscipline"
                        value={formData.otherDiscipline}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Enter your discipline"
                      />
                    </div>
                  )}

                  {/* Review Availability - Styled as Blocks */}
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-3">
                      Are you available to review for ASFIRJ?
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.reviewAvailability === 'yes' 
                          ? 'border-purple-600 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300 hover:bg-[#f6ecf6]'
                      }`}>
                        <input
                          type="radio"
                          name="reviewAvailability"
                          value="yes"
                          checked={formData.reviewAvailability === 'yes'}
                          onChange={handleRadioChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className={`text-lg font-semibold mb-1 ${
                            formData.reviewAvailability === 'yes' ? 'text-purple-600' : 'text-gray-700'
                          }`}>
                            Yes
                          </div>
                          <div className={`text-sm ${
                            formData.reviewAvailability === 'yes' ? 'text-purple-600' : 'text-gray-500'
                          }`}>
                            I'm available to review
                          </div>
                          {formData.reviewAvailability === 'yes' && (
                            <div className="absolute top-2 right-2 text-purple-600">
                              <Check className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                      </label>

                      <label className={`relative flex items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        formData.reviewAvailability === 'no' 
                          ? 'border-purple-600 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300 hover:bg-[#f6ecf6]'
                      }`}>
                        <input
                          type="radio"
                          name="reviewAvailability"
                          value="no"
                          checked={formData.reviewAvailability === 'no'}
                          onChange={handleRadioChange}
                          className="sr-only"
                        />
                        <div className="text-center">
                          <div className={`text-lg font-semibold mb-1 ${
                            formData.reviewAvailability === 'no' ? 'text-purple-600' : 'text-gray-700'
                          }`}>
                            No
                          </div>
                          <div className={`text-sm ${
                            formData.reviewAvailability === 'no' ? 'text-purple-600' : 'text-gray-500'
                          }`}>
                            Not available to review
                          </div>
                          {formData.reviewAvailability === 'no' && (
                            <div className="absolute top-2 right-2 text-purple-600">
                              <Check className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Account Information */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  {/* Password Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div className="relative">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        Create Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 pr-10 ${
                          formErrors.password ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Password (min. 8 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                      {formErrors.password && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.password}</p>
                      )}
                    </div>

                    <div className="relative">
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Confirm Password <span className="text-red-500">*</span>
                      </label>
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 pr-10 ${
                          formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Confirm Password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                      {formErrors.confirmPassword && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.confirmPassword}</p>
                      )}
                    </div>
                  </div>

                  {/* Password Requirements */}
                  <div className="text-xs text-gray-500 bg-[#f6ecf6] p-4 rounded-lg">
                    <p className="font-medium mb-2">Password must contain:</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>At least 8 characters</li>
                      <li>At least one uppercase letter</li>
                      <li>At least one lowercase letter</li>
                      <li>At least one number</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePreviousStep}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                )}
                
                {currentStep < 3 ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2 ml-auto"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={processing}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto"
                  >
                    {processing ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Create Account & {action === 'accept' ? 'Accept' : 'Decline'}
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Cancel Button */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel and return to home
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main invitation page
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        {/* Error/Success messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
            <p className="text-sm text-green-700">{successMessage || success}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className={`px-6 py-4 ${
            isProcessed || isExpired ? 'bg-gray-600' : (action === 'accept' ? 'bg-green-600' : 'bg-red-600')
          }`}>
            <div className="flex items-center">
              {isProcessed || isExpired ? (
                invitationStatus === 'accepted' ? (
                  <CheckCircle className="w-8 h-8 text-white mr-3" />
                ) : invitationStatus === 'rejected' ? (
                  <XCircle className="w-8 h-8 text-white mr-3" />
                ) : (
                  <Hourglass className="w-8 h-8 text-white mr-3" />
                )
              ) : action === 'accept' ? (
                <CheckCircle className="w-8 h-8 text-white mr-3" />
              ) : (
                <XCircle className="w-8 h-8 text-white mr-3" />
              )}
              <div>
                <h2 className="text-xl font-semibold text-white">
                  {isProcessed || isExpired ? 'Invitation Already Processed' : (action === 'accept' ? 'Accept Invitation' : 'Decline Invitation')}
                </h2>
                <p className="text-white text-sm opacity-90">
                  {isProcessed || isExpired 
                    ? statusMessage || 'This invitation has already been processed'
                    : `You've been invited to ${type === 'reviewer' ? 'review' : 'edit'} this manuscript`}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Status Message for Processed/Expired Invitations */}
            {(isProcessed || isExpired) && statusMessage && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center">
                  {invitationStatus === 'accepted' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  ) : invitationStatus === 'rejected' ? (
                    <XCircle className="w-5 h-5 text-red-600 mr-2" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                  )}
                  <p className="text-sm text-yellow-800">{statusMessage}</p>
                </div>
              </div>
            )}

            {/* Manuscript Info */}
            {submissionData && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-700 mb-3 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-purple-600" />
                  Manuscript Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Title</p>
                    <p className="text-sm font-medium text-gray-800">{submissionData.title}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Manuscript ID</p>
                    <p className="text-sm font-medium text-gray-800">{articleId}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="text-sm font-medium text-gray-800">{submissionData.article_type || 'Research Article'}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Submitted Date</p>
                    <p className="text-sm font-medium text-gray-800">{formatDate(submissionData.created_at || submissionData.date_submitted)}</p>
                  </div>
                  
                  {submissionData.corresponding_author_fullname && (
                    <div>
                      <p className="text-sm text-gray-500">Corresponding Author</p>
                      <p className="text-sm font-medium text-gray-800">{submissionData.corresponding_author_fullname}</p>
                    </div>
                  )}
                  
                  {submissionData.corresponding_author_email && (
                    <div>
                      <p className="text-sm text-gray-500">Corresponding Email</p>
                      <p className="text-sm font-medium text-gray-800">{submissionData.corresponding_author_email}</p>
                    </div>
                  )}
                </div>

                {authors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-500">Authors</p>
                    <p className="text-sm font-medium text-gray-800">{formatAuthors(authors)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Invitation Info */}
            {invitationData && (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                <h3 className="font-medium text-purple-700 mb-3 flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Invitation Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-purple-600">Invited by</p>
                    <p className="text-sm font-medium text-purple-800">{invitationData.invited_by_name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-purple-600">Invited on</p>
                    <p className="text-sm font-medium text-purple-800">{formatDate(invitationData.invited_at)}</p>
                  </div>
                  
                  {invitationData.expires_at && (
                    <div>
                      <p className="text-sm text-purple-600">Expires on</p>
                      <p className="text-sm font-medium text-purple-800">{formatDate(invitationData.expires_at)}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-purple-600">Status</p>
                    <p className="text-sm font-medium text-purple-800 capitalize">
                      {isExpired ? 'expired' : invitationData.status}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Message - Only show for active invitations */}
            {!isProcessed && !isExpired && invitationData && (
              <div className={`mb-6 p-4 rounded-lg ${
                action === 'accept' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm ${
                  action === 'accept' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {action === 'accept' 
                    ? 'By accepting this invitation, you agree to provide a thorough review of this manuscript within the specified timeframe.'
                    : 'By declining this invitation, you will not be required to review this manuscript. The editor will be notified of your decision.'}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleInvitationAction}
                disabled={isActionDisabled()}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                  isProcessed || isExpired 
                    ? 'bg-gray-400 cursor-not-allowed'
                    : (action === 'accept' 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-red-600 hover:bg-red-700')
                }`}
              >
                {processing ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {action === 'accept' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                    {getButtonText()}
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>

            {/* Account Note */}
            {!userExists && !isProcessed && !isExpired && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Note: You'll need to create an account to {action} this invitation.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationHandler;